import clsx from 'clsx';
import debounce from 'lodash/debounce';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import InnerHTMLDiv from '../common/inner-html-div.component';
import OverlayPanel from '../common/overlay-panel/overlay-panel.component';
import { DomElementAlignment, DomTargetPosition } from '../enums';

const isTouchDevice = typeof matchMedia !== 'undefined' && matchMedia('(hover: none), (pointer: coarse)').matches;

export interface TooltipLegacyProps {
  description: string | ReactNode;
  lazyLoadDescription?: boolean;
  arrow?: boolean;
  disabled?: boolean;
  tooltipTargetPosition?: DomTargetPosition;
  tooltipAlignment?: DomElementAlignment;
  closeTooltipOnClick?: boolean;
  tooltipZIndexCheck?: boolean;
  tooltipTransitionDuration?: number;
  tooltipClassName?: string;
  wrapperClassName?: string;
  debounceTime?: number;
  exitDebounceTime?: number;
  dependOnChildren?: boolean;
  shouldUseCreatePortal?: boolean;
  /**
   * Enables tooltip interaction on mobile devices via touch/tap.
   * Desktop hover behavior is unaffected by this prop.
   * @default false
   */
  enableMobileTouch?: boolean;
  renderAsInnerHTML?: boolean; // render description as inner html
  children: ReactNode | Array<ReactNode>;
}

export default function TooltipLegacy({
  description,
  lazyLoadDescription = false,
  arrow,
  disabled,
  tooltipTargetPosition = DomTargetPosition.BottomLeft,
  tooltipAlignment = DomElementAlignment.TopLeft,
  closeTooltipOnClick = false,
  tooltipZIndexCheck = false,
  tooltipTransitionDuration = 400,
  tooltipClassName = '',
  wrapperClassName = 'flex items-center',
  debounceTime = 500,
  exitDebounceTime = 100,
  dependOnChildren = false,
  shouldUseCreatePortal = true,
  enableMobileTouch = false,
  renderAsInnerHTML = false,
  children,
}: TooltipLegacyProps) {
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const messageRef = useRef<HTMLDivElement>(null);

  // Use refs to hold the current debounced functions, allowing event handlers to always
  // call the latest version while useEffect manages creation and cleanup
  const onTooltipEnterRef = useRef<ReturnType<typeof debounce> | null>(null);
  const onTooltipExitRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Create and cleanup onTooltipExit debounced function
  useEffect(() => {
    const fn = debounce(() => {
      onTooltipEnterRef.current?.cancel();
      setTooltipVisible(false);
    }, exitDebounceTime);

    onTooltipExitRef.current = fn;

    return () => {
      fn.cancel();
    };
  }, [exitDebounceTime]);

  // Create and cleanup onTooltipEnter debounced function
  // Note: disabled is checked at invocation time (in onTooltipEnter), not here,
  // so tooltip won't show if disabled becomes true while debounce is pending
  useEffect(() => {
    const fn = debounce(() => {
      setTooltipVisible(true);
    }, debounceTime);

    onTooltipEnterRef.current = fn;

    return () => {
      fn.cancel();
    };
  }, [debounceTime]);

  // Event handler for mouse enter - checks disabled at invocation time
  const onTooltipEnter = () => {
    if (disabled || isTouchDevice) return;

    if (dependOnChildren) {
      // For nested tooltips, cancel any pending exit and show immediately (bypass debounce)
      onTooltipExitRef.current?.cancel();
      setTooltipVisible(true);
    } else {
      onTooltipEnterRef.current?.();
    }
  };

  const onTooltipExit = () => {
    onTooltipExitRef.current?.();
  };

  const onOverlayPanelEnter = () => {
    if (dependOnChildren) {
      onTooltipExitRef.current?.cancel();
    }
  };

  const tooltipDescription = () => {
    if (lazyLoadDescription) {
      if (tooltipVisible) {
        return description;
      }
      return null;
    } else {
      return description;
    }
  };

  const handleClick = () => {
    if (isTouchDevice && enableMobileTouch && !disabled) {
      setTooltipVisible((prev) => !prev);
    }
  };

  const tooltipPositionStyles: Record<number, string> = {
    [DomTargetPosition.Left]: '-translate-y-1/2 sm:-ml-3',
    [DomTargetPosition.Right]: '-translate-y-1/2 sm:ml-3',
  };

  const arrowDirectionStyles: Record<number, string> = {
    [DomTargetPosition.Left]: '-right-2.5 arrow-right',
    [DomTargetPosition.Right]: '-left-2.5 arrow-left',
  };

  return (
    <div
      ref={messageRef}
      onMouseEnter={onTooltipEnter}
      onMouseLeave={onTooltipExit}
      onClick={handleClick}
      className={wrapperClassName}
    >
      {children}
      <OverlayPanel
        visible={tooltipVisible}
        hidden={onTooltipExit}
        shouldScrollCloseOverlay={true}
        target={messageRef.current}
        targetPosition={tooltipTargetPosition}
        elementAlignment={tooltipAlignment}
        shouldCheckZIndex={tooltipZIndexCheck}
        transitionDuration={tooltipTransitionDuration}
        onMouseEnter={onOverlayPanelEnter}
        shouldRemainOnScreen={true}
        className={clsx(
          'rounded-sm px-2.5 py-1.5 drop-shadow-sm bg-mono-1',
          tooltipPositionStyles[tooltipTargetPosition],
          tooltipClassName
        )}
        shouldUseCreatePortal={shouldUseCreatePortal}
      >
        {arrow && (
          <div
            className={clsx(
              'absolute top-1/2 -mt-1.5 h-2.5 w-2.5 bg-mono-1',
              arrowDirectionStyles[tooltipTargetPosition]
            )}
          />
        )}
        {renderAsInnerHTML ? (
          <InnerHTMLDiv
            html={String(tooltipDescription() || '')}
            onClick={() => {
              if (closeTooltipOnClick) {
                onTooltipExit();
              }
            }}
          />
        ) : (
          <div
            onClick={() => {
              if (closeTooltipOnClick) {
                onTooltipExit();
              }
            }}
          >
            {tooltipDescription()}
          </div>
        )}
      </OverlayPanel>
    </div>
  );
}
